import { useEffect, useRef, useState } from 'react';
import { ClipboardList, FileUp, Camera, Plus } from 'lucide-react';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/useToast';
import { useParse } from '@/hooks/useParse';

interface ImportBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function ImportBottomSheet({ open, onOpenChange }: ImportBottomSheetProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const parseMutation = useParse();
  const [isParsing, setIsParsing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast('File too large. Maximum size is 10MB.', 'error');
      e.target.value = '';
      return;
    }

    onOpenChange(false); // close the bottom sheet
    setIsParsing(true);

    try {
      let content: string;
      let contentType: string;
      let mimeType: string | undefined;

      if (file.name.endsWith('.txt')) {
        content = await file.text();
        contentType = 'text';
      } else if (file.type === 'application/pdf') {
        content = await fileToBase64(file);
        contentType = 'pdf_base64';
        mimeType = 'application/pdf';
      } else {
        // image
        content = await fileToBase64(file);
        contentType = 'image_base64';
        mimeType = file.type;
      }

      const result = await parseMutation.mutateAsync({ content, contentType, mimeType });
      sessionStorage.setItem('wedtrack:parsed', JSON.stringify(result.events));
      navigate('/schedule/import/review');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to parse file';
      toast(message, 'error');
    } finally {
      setIsParsing(false);
      // Reset file input
      e.target.value = '';
    }
  }

  if (!open && !isParsing) return null;

  const options = [
    {
      icon: <ClipboardList size={20} style={{ color: 'var(--color-primary)' }} />,
      label: 'Paste Text',
      action: () => {
        onOpenChange(false);
        navigate('/schedule/import/paste');
      },
    },
    {
      icon: <FileUp size={20} style={{ color: 'var(--color-primary)' }} />,
      label: 'Upload File',
      action: () => {
        fileInputRef.current?.click();
      },
    },
    {
      icon: <Camera size={20} style={{ color: 'var(--color-primary)' }} />,
      label: 'Take Photo',
      action: () => {
        cameraInputRef.current?.click();
      },
    },
    {
      icon: <Plus size={20} style={{ color: 'var(--color-primary)' }} />,
      label: 'Add Single Event',
      action: () => {
        onOpenChange(false);
        navigate('/schedule/event/new');
      },
    },
  ];

  return (
    <>
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.txt,.jpg,.jpeg,.png,.webp"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />

      {open && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => onOpenChange(false)}
            aria-label="Close import menu"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.7)',
              zIndex: 800,
            }}
          />

          {/* Bottom sheet panel */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Import timeline"
            className="animate-slide-up"
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'var(--color-surface)',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              padding: '16px 0',
              paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
              zIndex: 801,
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            {/* Drag handle */}
            <div
              style={{
                width: 40,
                height: 4,
                background: 'rgba(148,163,184,0.3)',
                borderRadius: 2,
                margin: '0 auto 16px',
              }}
            />

            {/* Sheet title */}
            <div
              style={{
                paddingLeft: 16,
                paddingRight: 16,
                marginBottom: 8,
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                  fontWeight: 500,
                }}
              >
                Add to Schedule
              </p>
            </div>

            {/* Option rows */}
            {options.map((option) => (
              <button
                key={option.label}
                onClick={option.action}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  width: '100%',
                  minHeight: 56,
                  padding: '0 16px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: 'var(--color-text)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 15,
                  transition: 'background 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--color-surface-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                {option.icon}
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Parsing loading overlay */}
      {isParsing && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 900,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            background: 'rgba(26, 26, 46, 0.9)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <div
            className="animate-spin"
            style={{
              width: 40,
              height: 40,
              border: '3px solid rgba(212,168,75,0.2)',
              borderTopColor: 'var(--color-primary)',
              borderRadius: '50%',
            }}
          />
          <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', margin: 0 }}>
            Parsing your file…
          </p>
        </div>
      )}
    </>
  );
}
